// Importing historical runs on Claims → Runs: every RA PDF in the Drive
// Remittance Advice folder not yet imported, each parsed and written as a
// closed run by the import-ra-run function.
//
// Moved out of pages/finance/BillingRun.js on 2026-10-20. Nothing is imported
// until "Import N Files"; already-imported files are skipped by the function.

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

/**
 * @param {Function} p.onClose
 * @param {Function} p.onImported  after the batch finishes, to reload the run list
 */
export default function BackfillModal({ onClose, onImported }) {
  const [backfillFiles,  setBackfillFiles]  = useState([]);
  const [backfillStatus, setBackfillStatus] = useState({}); // fileId → {state, message}
  const [backfillLoading, setBackfillLoading] = useState(true);
  const [backfillRunning, setBackfillRunning] = useState(false);

  // ── Backfill handlers ─────────────────────────────────────────────────────
  const loadFiles = async () => {
    setBackfillLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/list-ra-files`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: '{}',
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Failed to list files');
      setBackfillFiles(data.files || []);
    } catch (err) {
      console.error('Backfill list error:', err);
    } finally {
      setBackfillLoading(false);
    }
  };

  const runBackfill = async (filesToImport) => {
    setBackfillRunning(true);
    const { data: { session } } = await supabase.auth.getSession();
    const base = process.env.REACT_APP_SUPABASE_URL;
    const headers = {
      'content-type': 'application/json',
      'Authorization': `Bearer ${session?.access_token}`,
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
    };
    for (const f of filesToImport) {
      setBackfillStatus(prev => ({ ...prev, [f.file_id]: { state: 'running', message: 'Importing…' } }));
      try {
        const res = await fetch(`${base}/functions/v1/import-ra-run`, {
          method: 'POST', headers,
          body: JSON.stringify({
            file_id: f.file_id,
            filename: f.filename,
            service_from: f.service_from,
            service_to: f.service_to,
            web_view_link: f.web_view_link,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Import failed');
        if (data.skipped) {
          setBackfillStatus(prev => ({ ...prev, [f.file_id]: { state: 'skipped', message: 'Already imported' } }));
        } else {
          setBackfillStatus(prev => ({
            ...prev,
            [f.file_id]: { state: 'done', message: `${data.lines_created} lines · $${(data.total_paid || 0).toFixed(2)} paid` },
          }));
        }
      } catch (err) {
        setBackfillStatus(prev => ({ ...prev, [f.file_id]: { state: 'error', message: err.message } }));
      }
    }
    setBackfillRunning(false);
    // Refresh billing runs list
    onImported();
  };

  // The list is read when the dialog opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadFiles(); }, []);

  return (
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && !backfillRunning && onClose()}>
        <div className="modal" style={{ maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">📥 Import Historical RAs from Drive</div>
            <button className="btn btn-ghost btn-sm" onClick={() => onClose()} disabled={backfillRunning}>✕</button>
          </div>
          <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
            {backfillLoading ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <div className="loading-spinner" style={{ margin: '0 auto 12px' }} />
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading files from Drive…</div>
              </div>
            ) : backfillFiles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>No PDF files found in the Remittance Advice folder.</div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                  {backfillFiles.filter(f => !f.already_imported).length} of {backfillFiles.length} files not yet imported.
                  {' '}Files already imported are shown in green.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {backfillFiles.map(f => {
                    const st = backfillStatus[f.file_id];
                    const isRunning = st?.state === 'running';
                    const isDone = st?.state === 'done';
                    const isError = st?.state === 'error';
                    const isSkipped = st?.state === 'skipped' || f.already_imported;
                    const rowBg = isDone ? 'var(--success-bg)' : isError ? 'var(--danger-bg)' : isSkipped ? 'var(--bg2)' : 'var(--bg)';
                    const rowBorder = isDone ? '1px solid var(--success)' : isError ? '1px solid var(--danger)' : '1px solid var(--border)';
                    return (
                      <div key={f.file_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: rowBg, border: rowBorder, borderRadius: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {f.service_from} → {f.service_to}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                            {st?.message || (isSkipped && !st ? 'Already imported' : '')}
                          </div>
                        </div>
                        {isRunning && <div className="loading-spinner" style={{ width: 16, height: 16, flexShrink: 0 }} />}
                        {isDone && <span style={{ color: 'var(--success)', fontSize: 14 }}>✓</span>}
                        {isError && <span style={{ color: 'var(--danger)', fontSize: 14 }}>✕</span>}
                        {isSkipped && !st && <span style={{ color: 'var(--text3)', fontSize: 11 }}>✓ done</span>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              Each file is parsed by Claude AI — allow ~10–15s per file
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => onClose()} disabled={backfillRunning}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                disabled={backfillRunning || backfillLoading || backfillFiles.filter(f => !f.already_imported && backfillStatus[f.file_id]?.state !== 'done').length === 0}
                onClick={() => runBackfill(backfillFiles.filter(f => !f.already_imported && backfillStatus[f.file_id]?.state !== 'done'))}
              >
                {backfillRunning ? 'Importing…' : `Import ${backfillFiles.filter(f => !f.already_imported && backfillStatus[f.file_id]?.state !== 'done').length} Files`}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
}
